import Head from 'next/head'
import Script from 'next/script'
import Image from 'next/image'
import { s3Static } from '../../../utils/constants'
import { contentDate } from '../../../utils/article-date-time'
import { blogsMaster } from '../../../utils/sg-blogs-data'
import { getRequestJSON } from '../../../api/index/location-api'
import { FaCheckCircle, FaStar, FaWhatsapp } from 'react-icons/fa'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RequestProvider } from '../../../context/RequestContext'
import { Services } from '../../../components/index/service/Services'
import { AnimatePresence, motion } from 'framer-motion'
import { getWpNewHouseKeeping, setWpNewHouseKeeping } from '../../../service/wp-blogs-store'
import { getWpNewPostsByCategory } from '../../../lib/service'
import { getAppSettings } from '../../../api/app-setting/app-setting'
import { DeepCleanBooking } from '../../../components/index/service-category/domestic-service/DeepCleanBooking'
import { openModal } from '../../../service/modal-service'
import { SystemError } from '../../../components/error/SystemError'
import { FaKitchenSet } from "react-icons/fa6";
import { MdBalcony, MdOutlineBathroom, MdOutlineBedroomParent, MdOutlineLiving } from 'react-icons/md'
import { professionalCleaningTestimonials } from '../../../data/CustomerReviewData'
import { HelperWhyChooseUs } from '../../../components/index/service-category/domestic-service/HelperWhyChooseUs'
import { CustomerReviews } from '../../../components/index/commons/CustomerReviews'

// When this page's content last actually changed. Bump it by hand when you
// edit the copy - not for layout/UX changes. Feeds dateModified and
// article:modified_time (see reviewedIso in utils/article-date-time).
const CONTENT_REVIEWED = '2026-09-25'

export async function getStaticProps() {
    const countryCd = "sg"
    const countryName = "Singapore"
    
    const allRequestCategorySettings = await getRequestJSON(countryName)

    const requestCategorySettings = allRequestCategorySettings.filter(category => category.requestType === "Domestic Services")
    
    const lat = 1.3521
    const lng = 103.8198

    const { modifiedDate, month, year } = contentDate(CONTENT_REVIEWED)
    const houseKeepingBlogs = blogsMaster['house-keeping'].blogArray;
    const wpNewPosts = getWpNewHouseKeeping().length > 0 ?  getWpNewHouseKeeping() : await getWpNewPostsByCategory(6, "house-keeping");
    setWpNewHouseKeeping(wpNewPosts)

    const currencyCd = "SGD"

    return {
        props: {
            requestCategorySettings,
            modifiedDate,
            month,
            year,
            houseKeepingBlogs,
            currencyCd,
            professionalCleaningTestimonials,
            wpNewPosts,
            countryName
        },
        // Next.js will attempt to re-generate the page:
        // - When a request comes in
        // - At most once every 10 seconds
        revalidate: 259200, // In seconds
    }
}
export default function Home(props) {
    const router = useRouter()
    const modifiedDate = props.modifiedDate
    const month = props.month
    const year = props.year
    const houseKeepingBlogs = props.houseKeepingBlogs
    const wpNewPosts = props.wpNewPosts
    const requestCategorySettings = props.requestCategorySettings;
    const testimonials = props.professionalCleaningTestimonials
    const maxRating = [1, 2, 3, 4, 5]
    const requestType = 'Domestic Services'
    const countryName = props.countryName
    const currencyCd = props.currencyCd

    const [ requestForm, setRequestForm ] = useState(router.query.requestForm)
    const [ waInd, setWaInd ] = useState(false)
    const [ isQuickBook, setIsQuickBook ] = useState(false)
    const [ openIndex, setOpenIndex ] = useState(null);

    const [ isSystemError, setIsSystemError ] = useState(false);
    const [ errorContent, setErrorContent ]  = useState({'header': "", 'body': ""})

    const cleaningTools = [
        { icon: "🧹", name: "Vacuum Cleaner", desc: "Removes dust, dirt, and debris from floors and surfaces.", type: "Dry" },
        { icon: "🧽", name: "Microfiber Cloths", desc: "Absorbent and effective at capturing dust and grime.", type: "Dry" },
        { icon: "🪣", name: "Mop and Bucket", desc: "Used for cleaning hard floors, such as tile or wood.", type: "Wet" },
        { icon: "🪥", name: "Scrub Brushes", desc: "Helps in scrubbing tough stains and grime from surfaces.", type: "Wet" },
        { icon: "🪟", name: "Squeegee", desc: "Used for cleaning windows and glass surfaces.", type: "Wet" },
        { icon: "🧴", name: "Spray Bottles", desc: "Contains cleaning solutions for various surfaces.", type: "Multipurpose" },
        { icon: "🕸️", name: "Duster", desc: "Used for dusting surfaces such as furniture and shelves.", type: "Dry" },
        { icon: "🧺", name: "Cleaning Caddies", desc: "Holds cleaning supplies and tools for easy transport.", type: "Multipurpose" },
        { icon: "📏", name: "Extension Poles", desc: "Extends the reach for cleaning high or hard-to-reach areas.", type: "Dry" },
        { icon: "🌫️", name: "Steam Cleaner", desc: "Utilizes steam to sanitize and clean various surfaces.", type: "Wet" },
        { icon: "🔬", name: "Grout Brush", desc: "Specifically designed for cleaning grout lines.", type: "Wet" },
        { icon: "🧼", name: "Carpet Extractor", desc: "Used for deep cleaning carpets and rugs.", type: "Wet" },
        { icon: "🧪", name: "Disinfectant Solution", desc: "Kills germs and bacteria on surfaces.", type: "Multipurpose" },
        { icon: "🧤", name: "Gloves", desc: "Protects hands from chemicals and germs.", type: "Multipurpose" },
        { icon: "🧹", name: "Dustpan and Brush", desc: "Collects debris swept from floors.", type: "Dry" },
    ];

    const typeColors = {
        Dry: "bg-blue-100 text-blue-800",
        Wet: "bg-green-100 text-green-800",
        Multipurpose: "bg-purple-100 text-purple-800",
    };

    useEffect(() => {
        setRequestForm(router.query.requestForm)
    }, [router.query.requestForm])

    const videoRef = useRef();

    useEffect(() => {    
        if(requestForm) return
        videoRef.current.play()
    }, [requestForm])

    const openRequestForm = () => {
        window.scrollTo({ top: 0 });
        // setRequestForm(true)
        openModal(setRequestForm)
    }
    
    const fetchAppSetting = useCallback(async() => {
        try {
            const appSettings = await getAppSettings(countryName)
            setWaInd(appSettings.waBusinessInd)
        } catch (error) {
            console.log(error);
        }
    }, [countryName])

    useEffect(() => {
        if(!countryName) return

        fetchAppSetting()
    }, [countryName, fetchAppSetting])

    const showJobScope = () =>{
        setErrorContent({'header': "Service Inclusions", 'isInfo': true, 'body': "All Rooms - Wipe all surfaces, including aircon. Clean windows, frames, and sills. Wipe cabinet interiors and shelves if empty. Clean ceiling fans and light fixtures. Vacuum and mop.\n\nKitchen - Clean sinks, dining tables, walls, kitchen appliances, dispose of garbage bags.\n\nBathrooms - Clean toilet bowls, bathtub/shower, glass surfaces, mirrors, basin, tap, ventilation fan, floor, drains, wall tiles, and door. Wipe racks and drawers if empty. Dispose of garbage bags.\n\nBedrooms - Wipe racks and drawers if empty. Dispose of garbage bags.\n\nOther - Wipe glass windows, metal grilles (if any), and clean washing machine exterior."});
        openModal(setIsSystemError)
    }

  return (
    <>
      <Head>
        <title>Professional House Cleaning Service Singapore</title>
        <meta charSet="utf-8"/>
        <meta httpEquiv="X-UA-Compatible" content="IE=edge"/>
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="description" content="Enjoy healthy living with professional house cleaning service in Singapore. Our expert cleaners will ensure a clean and hygienic home space for you and you family. Book now!"/>
        <meta name="keywords" content="Professional House Cleaning Service Singapore"/>
        <meta name="author" content="StringsSG"/>
        <meta name="google-site-verification" content="JAjj5vAc8Xxrh4idRAchZhYR6rwxWPAk3vxMdx-dwVQ" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:url"   content="https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning" />
        <meta property="og:type"   content="article" />
        <meta property="og:title"    content={`Professional House Cleaning Service Singapore`}/>
        <meta property="og:description" content="Enjoy healthy living with professional house cleaning service in Singapore. Our expert cleaners will ensure a clean and hygienic home space for you and you family. Book now!" />
        <meta property="article:publisher" content="https://www.facebook.com/StringsSG/" />
        <meta property="article:published_time" content="2025-01-21T07:30:46+00:00" />
        <meta property="article:modified_time" content={modifiedDate} />
        <meta property="og:image"  content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/part-time-helper/home-cleaner-clip-art.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/part-time-helper/home-cleaner-clip-art.jpg" />
        <meta name="twitter:site" content="@stringssg"/> 
        <meta name="twitter:url" content="https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning"/>
        <meta name="twitter:title" content={`Professional House Cleaning Service Singapore`}/> 
        <meta name="twitter:description" content="Enjoy healthy living with professional house cleaning service in Singapore. Our expert cleaners will ensure a clean and hygienic home space for you and you family. Book now!"/>
        <link rel="icon" href="/favicon.ico" />
        <script type="application/ld+json"
          dangerouslySetInnerHTML={{ __html:
            `{"@context":"https://schema.org","@graph":[{"@context":"https://schema.org/","@type":"BlogPosting","mainEntityOfPage":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning"},"headline":"Professional House Cleaning Service Singapore","description":"Enjoy healthy living with professional house cleaning service in Singapore. Our expert cleaners will ensure a clean and hygienic home space for you and you family. Book now!","image":["https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/blog/living/part-time-helper/home-cleaner-clip-art.jpg"],"author":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/"},"publisher":{"@type":"Organization","@id":"https://www.stringssg.com/#organization","name":"StringsSG","url":"https://www.stringssg.com/","logo":{"@type":"ImageObject","url":"https://stringssg-assets.s3-ap-southeast-1.amazonaws.com/static/logo.png"}},"datePublished":"2025-01-21T07:30:46+00:00","dateModified":"${modifiedDate}"},{"@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/","name":"Home","url":"https://www.stringssg.com/"}},{"@type":"ListItem","position":2,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/part-time-cleaner-singapore","name":"Part Time Cleaner Singapore","url":"https://www.stringssg.com/sg/part-time-cleaner-singapore"}},{"@type":"ListItem","position":3,"item":{"@type":"WebPage","@id":"https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning","name":"Professional House Cleaning","url":"https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning"}}]}]}`
          }}
        />
        <link rel="canonical" href="https://www.stringssg.com/sg/part-time-cleaner-singapore/professional-house-cleaning"/>
        </Head>
        <section id="professional-house-cleaning-singapore" className='relative'>
            <div className="-mt-14 relative w-full aspect-video sm:h-180 overflow-hidden">
                {/* Mobile Video */}
                <video
                    ref={videoRef}
                    className="absolute inset-0 w-full h-full object-cover sm:hidden"
                    poster={s3Static + "blog/living/part-time-helper/housekeeping-1920-1080.jpg"}
                    title="Professional House Cleaning Singapore"
                    muted
                    loop
                    playsInline
                    preload="metadata"
                >
                    <source src="/video/part-time-helper-web.mp4" />
                    <source src="/video/part-time-helper-web.mov" />
                </video>

                {/* Desktop Image */}
                <Image
                    src={s3Static + "blog/living/part-time-helper/part-time-cleaners-singapore.jpg"}
                    fill
                    className="hidden sm:block object-cover"
                    alt="Professional house cleaning in Singapore"
                    priority={false}
                    quality={100}
                    sizes="100vw"
                />
            </div>
            <RequestProvider>
                <div id="campaign" className='relative bg-gray-100 sm:bg-transparent sm:text-white w-full py-2 sm:absolute sm:top-1/2 sm:-translate-y-1/2'>
                    <DeepCleanBooking requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} setIsQuickBook={setIsQuickBook} countryName={countryName} currencyCd={currencyCd} deepCleanType="Quick Booking - Home Cleaning" showJobScope={showJobScope} serviceType="Spring cleaning"/>
                </div>
                {requestForm &&
                    <Services requestType={requestType} requestCategorySettings={requestCategorySettings} setRequestForm={setRequestForm} isQuickBook={isQuickBook} asSheet/>
                }      
            </RequestProvider>  
        </section>
        <section className="max-w-5xl mx-auto py-12 px-4 space-y-6 text-center">
            <h1 className="text-3xl md:text-4xl leading-tight">
                <strong>Affordable & Reliable House Cleaning Services in Singapore</strong>
            </h1>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto pt-4">
                A clean home is more than just visually pleasing—it&apos;s vital for your health and well-being. StringsSG provides professional house cleaning services that go beyond basic tidying. With trained cleaners, clear pricing, and comprehensive service inclusions, we make it easy to enjoy a spotless home without the hassle.
            </p>
        </section>
        <section className="max-w-5xl mx-auto px-4 pt-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold uppercase">What is Professional House Cleaning?</h2>
            <div className="grid md:grid-cols-2 gap-6 items-center py-8">
                <p className=" text-left text-base leading-7">
                Professional house cleaning involves hiring trained and experienced cleaners to thoroughly sanitize and maintain your home&apos;s hygiene. StringsSG employs vetted professionals with the skills and equipment to clean every corner—from your living room to the bathroom—ensuring you get top-notch service and peace of mind.
                </p>

                <div className="relative h-80 w-full rounded-xl overflow-hidden shadow-lg">
                <Image
                    src={s3Static + "blog/living/part-time-helper/home-cleaner-clip-art.jpg"}
                    alt="Professional House Cleaning Singapore"
                    layout="fill"
                    objectFit="cover"
                    priority={false}
                    quality={100}
                />
                </div>
            </div>
        </section>
        <section className="max-w-5xl mx-auto py-12 px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center uppercase mb-10">
                What&apos;s Included in Our Cleaning Services?
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                {
                    icon: <MdOutlineLiving className="text-5xl text-orange-500" />, 
                    title: "Living Area",
                    desc: "Wipe all surfaces, clean aircon exterior, windows, shelves, ceiling fans, vacuum and mop the floor."
                },
                {
                    icon: <MdOutlineBedroomParent className="text-5xl text-orange-500" />, 
                    title: "Bedrooms",
                    desc: "Clean all surfaces, windows, cabinets, dispose trash, vacuum and mop."
                },
                {
                    icon: <FaKitchenSet className="text-5xl text-orange-500" />, 
                    title: "Kitchen",
                    desc: "Wipe surfaces, clean appliances, cabinets, dining areas, and mop floors."
                },
                {
                    icon: <MdOutlineBathroom className="text-5xl text-orange-500" />, 
                    title: "Bathrooms",
                    desc: "Scrub toilets, basins, mirrors, walls, tiles, floors, and replace trash bags."
                },
                {
                    icon: <MdBalcony className="text-5xl text-orange-500" />, 
                    title: "Balcony",
                    desc: "Mop balcony, wipe glass windows, metal grilles (if any), and clean washing machine exterior."
                },
                {
                    icon: <FaCheckCircle className="text-5xl text-orange-500" />, 
                    title: "Others",
                    desc: "Additional cleaning requests or miscellaneous items will be evaluated and quoted individually. Our team ensures flexibility to meet your household needs."
                }
                ].map(({ icon, title, desc }, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition duration-300 text-center space-y-4 touch-manipulation">
                    <div className="flex justify-center">{icon}</div>
                    <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
                ))}
            </div>
        </section> 
        <section className="max-w-5xl mx-auto my-12 px-4 space-y-8">
            <h2 className="text-2xl md:text-3xl font-bold text-center uppercase">
                Professional House Cleaning Price
            </h2>
            <div className="rounded-xl bg-slate-100 p-6 text-gray-800 shadow-md leading-relaxed space-y-4">
                <p>
                    In <strong>Singapore</strong>, professional house cleaning services for a 
                    <span className="text-orange-600 font-semibold"> 1000 sqft</span> apartment typically cost 
                    <span className="text-orange-600 font-semibold"> $400–$600</span>. For a 
                    <span className="text-orange-600 font-semibold"> 1500 sqft</span> apartment, expect 
                    <span className="text-orange-600 font-semibold"> $550–$700</span>.
                </p>
                <p>
                    The cost varies depending on your home type — <strong>HDB</strong>, <strong>condo</strong>, or 
                    <strong> landed</strong> — and may differ across cleaning companies.
                </p>
            </div>

            <div className="rounded-xl bg-white p-6 shadow-md text-gray-800 space-y-4 border border-amber-200">
                <p>
                    <strong>StringsSG</strong> offers a smarter pricing model by calculating rates based on your 
                    <span className="text-orange-600 font-semibold"> actual floor size</span> instead of preset ranges. 
                    Our platform also adjusts pricing in real-time based on 
                    <span className="text-orange-600 font-semibold"> supply-demand analytics</span>.
                </p>
                <p>
                    Use our <strong className="text-orange-600">Instant Quotation Calculator</strong> to get 
                    an accurate price tailored to your home.
                </p>
                <div className="text-center pt-4">
                    <button
                        type="button"
                        onClick={() => scrollTo({ top: 0, behavior: 'smooth' })}
                        className="py-3 px-6 rounded-full bg-linear-to-tr from-amber-500 to-orange-400 text-white font-semibold shadow hover:scale-105 transition-transform duration-200"
                    >
                        Get My Instant Quote
                    </button>
                </div>
            </div>
        </section>
        <HelperWhyChooseUs />
        <section className="bg-white py-16 px-4">
            <div className="max-w-6xl mx-auto text-center">
                <h2 className="text-2xl md:text-3xl font-bold uppercase text-gray-800 mb-4 leading-relaxed">
                    What Customers Say About Our Professional Cleaning Services
                </h2>
                <p className="text-gray-600 mb-10 max-w-3xl mx-auto">
                    Real feedback from homeowners and businesses who trust <strong>StringsSG for home cleaning service in Singapore</strong>. Quality, speed, and customer satisfaction — all in one place.
                </p>
                <div className="flex justify-center items-center space-x-3 mb-4">
                    <Image
                        className="rounded-xl"
                        src={s3Static + "google-icon.png"}
                        height={45}
                        width={80}
                        style={{ objectFit: 'contain', objectPosition: 'center' }}
                        alt="Google Review for Aircon Servicing Singapore - StringsSG"
                    />
                    <div className="flex items-center space-x-1">
                        <span className="text-lg font-bold text-gray-500">4.8</span>
                        <span className="text-2xl text-amber-400">
                        <FaStar />
                        </span>
                    </div>
                </div>
                <CustomerReviews testimonials={testimonials} />
            </div>
        </section>
        <section className="max-w-5xl mx-auto my-20 px-4 space-y-10">
            <h2 className="text-2xl md:text-3xl font-bold uppercase text-center">
                Part-Time Helpers vs Professional House Cleaners
            </h2>

            <div className="bg-white rounded-xl shadow-md p-6 leading-relaxed text-gray-800 space-y-4">
                <p>
                While <strong>part-time helpers</strong> can assist with routine cleaning, they often lack the training and tools needed for deep, professional cleaning. In contrast, 
                <strong> StringsSG’s expert cleaners</strong> are trained to follow industry best practices using advanced equipment and techniques. This results in a more 
                <span className="text-orange-600 font-semibold"> consistent, efficient, and reliable</span> experience.
                </p>
                <p>
                Below is a detailed comparison of domestic helpers and professional house cleaners to help you decide the best option for your home.
                </p>
            </div>

            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-left border-collapse shadow-md">
                <thead className="bg-amber-100 text-amber-700 uppercase font-semibold">
                    <tr>
                    <th className="py-3 px-4 border">Aspect</th>
                    <th className="py-3 px-4 border">Domestic Helper</th>
                    <th className="py-3 px-4 border">Professional Cleaners</th>
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {[
                    {
                        aspect: "Experience",
                        helper: "May have limited professional training or experience.",
                        pro: "Trained and experienced in industry-grade cleaning techniques.",
                    },
                    {
                        aspect: "Quality of Work",
                        helper: "Depends on personal skill level.",
                        pro: "High and consistent quality due to training and protocols.",
                    },
                    {
                        aspect: "Flexibility",
                        helper: "Often flexible with tasks and timing.",
                        pro: "Follows structured schedules and scope.",
                    },
                    {
                        aspect: "Reliability",
                        helper: "Varies by individual.",
                        pro: "More reliable through managed scheduling and accountability.",
                    },
                    {
                        aspect: "Cost",
                        helper: "Lower upfront cost but less thorough.",
                        pro: "Higher cost, better value through efficiency and depth.",
                    },
                    {
                        aspect: "Equipment & Supplies",
                        helper: "Usually requires homeowner to provide materials.",
                        pro: "Brings professional tools and eco-friendly supplies.",
                    },
                    {
                        aspect: "Supervision",
                        helper: "May need close direction.",
                        pro: "Operates independently with minimal supervision.",
                    },
                    {
                        aspect: "Specialized Services",
                        helper: "Limited capability.",
                        pro: "Offers deep cleaning, disinfection, stain removal, etc.",
                    },
                    {
                        aspect: "Consistency",
                        helper: "Subject to availability and performance.",
                        pro: "Consistent quality and scheduling guaranteed.",
                    },
                    ].map((row, i) => (
                    <tr key={i} className="border-t even:bg-gray-50">
                        <td className="py-3 px-4 font-bold text-gray-700">{row.aspect}</td>
                        <td className="py-3 px-4">{row.helper}</td>
                        <td className="py-3 px-4">{row.pro}</td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>

            <figure className="relative w-full h-75 md:h-100 rounded-xl overflow-hidden shadow-md">
                <Image
                src={s3Static + "part-time-helper/female-home-cleaners.jpeg"}
                alt="Female House Cleaners - StringsSG"
                fill
                style={{ objectFit: "cover", objectPosition: "top" }}
                placeholder="blur"
                blurDataURL={s3Static + "placeholder.png"}
                />
                <figcaption className="absolute bottom-0 w-full bg-black/40 text-white text-center py-2 text-sm italic">
                    Female House Cleaners – StringsSG
                </figcaption>
            </figure>
        </section>
        <section className="max-w-5xl mx-auto my-100 px-4 space-y-8 leading-relaxed">
            <h2 className="text-2xl md:text-3xl font-bold uppercase text-center">
                How Often Should You Clean Your House?
            </h2>

            <p className="text-base">
                Here&apos;s a helpful table outlining how often different areas of your home should be cleaned in Singapore. It also indicates whether each task is suitable for a 
                <strong> part-time helper</strong> or if it requires a <strong>professional cleaning service</strong>:
            </p>

            <div className="overflow-x-auto rounded-xl shadow-md">
                <table className="min-w-full text-sm border border-gray-200">
                <thead className="bg-amber-100 text-amber-700 font-semibold text-center uppercase">
                    <tr>
                    <th className="p-3 border">Area of Home</th>
                    <th className="p-3 border">Cleaning Frequency</th>
                    <th className="p-3 border">Part-time Helper</th>
                    <th className="p-3 border">Professional Cleaner</th>
                    </tr>
                </thead>
                <tbody className="bg-white">
                    {[
                    { area: "Living Room", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Bedrooms", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Kitchen", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Bathrooms", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Dining Area", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Common Areas", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Windows", freq: "Every 1–2 weeks", helper: true, pro: true },
                    { area: "Carpets / Rugs", freq: "Every 3–6 months", helper: false, pro: true },
                    { area: "Upholstery", freq: "Every 6–12 months", helper: false, pro: true },
                    { area: "Deep Cleaning", freq: "Every 6 months", helper: false, pro: true },
                    ].map((row, i) => (
                    <tr key={i} className={`border-t ${i % 2 === 0 ? 'bg-gray-50' : ''}`}>
                        <td className="p-3 text-center font-medium text-gray-700">{row.area}</td>
                        <td className="p-3 text-center">{row.freq}</td>
                        <td className={`p-3 text-center text-lg ${row.helper ? 'text-green-600' : 'text-red-500'}`}>
                        {row.helper ? "✔️" : "✖️"}
                        </td>
                        <td className="p-3 text-center text-green-600 text-lg">✔️</td>
                    </tr>
                    ))}
                </tbody>
                </table>
            </div>

            <p className="text-base">
                While <strong>part-time helpers</strong> are great for weekly upkeep, certain tasks such as <em>deep cleaning</em>, <em>carpet/rug shampooing</em>, and <em>upholstery treatment</em> 
                require the tools and training of <strong>professional house cleaners</strong>. Scheduling these services regularly ensures a healthier and more hygienic living space.
            </p>
        </section>   
        <section className="max-w-5xl mx-auto my-20 px-4 space-y-6">
            <h2 className="text-2xl md:text-3xl font-bold uppercase text-center">
                Cleaning Tools Used in Professional House Cleaning
            </h2>

            <p>
                Professional cleaning services use a wide range of tools and equipment. Here’s a handy overview, categorized by their type of usage:
            </p>

            <div className="grid sm:grid-cols-2 gap-5">
                {cleaningTools.map((tool, i) => (
                <div key={i} className="rounded-xl border border-gray-200 shadow-md p-4 bg-white space-y-2">
                    <div
                    className="flex justify-between items-start cursor-pointer"
                    onClick={() => setOpenIndex(openIndex === i ? null : i)}
                    >
                    <div className="flex gap-3 items-center">
                        <span className="text-2xl">{tool.icon}</span>
                        <h3 className="text-base font-semibold">{tool.name}</h3>
                    </div>
                    <span className="text-sm text-gray-500">
                        {openIndex === i ? "▲" : "▼"}
                    </span>
                    </div>

                    {openIndex === i && (
                    <p className="text-gray-700">{tool.desc}</p>
                    )}

                    <span
                    className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${typeColors[tool.type]}`}
                    >
                    {tool.type}
                    </span>
                </div>
                ))}
            </div>

            <p className="pt-4">
                These are some of the essential tools used by professional house cleaners. Depending on your home’s needs and cleaning service type, the exact tools may vary.
            </p>
        </section>
        <section className="max-w-5xl mx-auto my-20 px-4 space-y-8">
            <h2 className="text-2xl md:text-3xl text-center font-bold uppercase">
                Benefits of Professional House Cleaning
            </h2>

            <div className="grid sm:grid-cols-2 gap-6">
                {[
                {
                    icon: "🎯",
                    title: "Expertise",
                    desc: "Trained professionals handle tough cleaning challenges with precision and efficiency.",
                },
                {
                    icon: "⏱️",
                    title: "Time-Saving",
                    desc: "Outsource chores to save time and focus on what matters most in your daily life.",
                },
                {
                    icon: "🛡️",
                    title: "Healthier Home",
                    desc: "Reduces allergens, dust, and bacteria for a cleaner, safer living environment.",
                },
                {
                    icon: "🧾",
                    title: "Customized Plans",
                    desc: "Get cleaning schedules tailored to your space, frequency, and specific needs.",
                },
                {
                    icon: "🧘",
                    title: "Peace of Mind",
                    desc: "Enjoy consistent quality and reliability from vetted professionals you can trust.",
                },
                {
                    icon: "✨",
                    title: "Professional Finish",
                    desc: "Your home looks and feels like new—cleaned with pro-grade tools and detail.",
                },
                ].map((item, i) => (
                <div
                    key={i}
                    className="flex flex-col items-center text-center p-6 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition space-y-4"
                >
                    <div className="text-5xl">{item.icon}</div>
                    <h3 className="text-lg font-semibold">{item.title}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.desc}</p>
                </div>
                ))}
            </div>
        </section>
        {/* <div className='max-w-5xl mx-auto text-center py-10 relative'>
            <div className='relative w-full h-96'>
                <Image
                    src={s3Static + "blog/living/part-time-helper/cleaner-with-toilet-cleaning-bucket.jpg"}
                    fill
                    style={{objectFit: 'cover', objectPosition: 'right'}} 
                    placeholder='blur'
                    blurDataURL={s3Static + "placeholder.png"}
                    alt='Domestic helper service Singapore - StringsSG'
                />     
            </div> 
            <div className='absolute flex items-center text-white top-0 bottom-0 left-0 right-0 w-fit mx-auto rounded-3xl'>
                <Link href="/sg/part-time-cleaner-singapore" prefetch={false} className='p-4 rounded-lg bg-black/60 text-xl md:text-2xl'>
                    Book Part Time Cleaner
                </Link>       
            </div>  
        </div>      */}
        <div className='fixed bottom-2 w-full text-center z-10 flex justify-center space-x-5'>
            {/* <a href={waInd ? "https://wa.me/6593512695" : "https://wa.me/6588491419" } target="_blank" rel="noreferrer" className='flex items-center text-white py-2 px-4 rounded-full bg-gradient-to-tl from-green-700 to-green-500 font-bold space-x-1'>
                <div>Chat</div>
                <FaWhatsapp className='text-2xl'/>
            </a> */}
        </div>   
        {/* <BlogsList blogs={houseKeepingBlogs} wpPosts={[]} wpNewPosts={wpNewPosts}/>    */}
        {!requestForm &&
            <div className='fixed bottom-2 w-full text-center z-10 flex justify-center space-x-5'>
                <button type="button" className='text-white py-2 px-4 rounded-full bg-linear-to-tr from-orange-600 to-amber-400 block font-bold ' onClick={() => openRequestForm()}>
                    Book Now
                </button>
                <a href={waInd ? "https://wa.me/6593512695" : "https://wa.me/6588491419" } target="_blank" rel="noreferrer" className='flex items-center text-white py-2 px-4 rounded-full bg-linear-to-tl from-green-700 to-green-500 font-bold space-x-1'>
                    <div>Chat</div>
                    <FaWhatsapp className='text-2xl'/>
                </a>
            </div> 
        } 
        <AnimatePresence>
            {isSystemError && <SystemError errorContent={errorContent} setIsSystemError={setIsSystemError}/>}
        </AnimatePresence>
        <Script 
          id="google-tagmanager-new"
          src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
          strategy="lazyOnload"
          onLoad={() => {              
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
          
            gtag('config', 'G-QD1HGGHDYJ');
          }} 
      />
      <Script id="facebook-tracking" strategy="lazyOnload">
          {`!function(f,b,e,v,n,t,s)
					  {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
					  n.callMethod.apply(n,arguments):n.queue.push(arguments)};
					  if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
					  n.queue=[];t=b.createElement(e);t.async=!0;
					  t.src=v;s=b.getElementsByTagName(e)[0];
					  s.parentNode.insertBefore(t,s)}(window, document,'script',
					  'https://connect.facebook.net/en_US/fbevents.js');
					  fbq('init', '128634041116395');
					  fbq('track', 'PageView');`}
        </Script>
    </>
  )
}